import { useState, useEffect } from 'react';
import { Plus, Camera, ScanLine, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import ServiceWithWorkflowToggle from './ServiceWithWorkflowToggle';
import { BookingService } from '../../lib/bookings';
import { useBusiness } from '../../contexts/BusinessContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { staffResourceId, upsellLineStamp } from '../../lib/upsellLines'; 
import { StockItem } from '../../lib/stockItems';
import { supabase } from '../../lib/supabase';
import { getCustomer } from '../../lib/customers';
import CreateVehicleModal from '../CreateVehicleModal';
import SelectVehicleModal from '../manage/SelectVehicleModal';
import SelectServiceModal from '../manage/SelectServiceModal';
import BookingPhotoCapture from './BookingPhotoCapture';
import { getBookingPhotos } from '../../services/bookingPhotoService';
import { CustomerVehicle } from '../../lib/customers';
import VinScanModal from '../VinScanModal';
import { isPlausibleVin } from '../../lib/vinDecode';

interface BookingServiceListProps {
  services: BookingService[];
  onAddService?: (vehicleId: string) => void;
  onAddProposal?: () => void;
  onEditVehicle?: (vehicleId: string, opts?: { scannedVin?: string }) => void;
  onUpdate: () => Promise<void>;
  onError: (error: string) => void;
  customer?: {
    id: string;
    vehicles?: any[];
  };
  showSalesInfo?: boolean;
  canEdit?: boolean;
  bookingWorkflows?: any[]; // Array of booking workflows
  bookingStatus?: string;
  onAssignWorkflow?: (serviceId: string) => void; // Callback to handle workflow assignment
  bookingId?: string; // Add booking ID for photo capture
}

export default function BookingServiceList({
  services,
  onAddService,
  onAddProposal,
  onEditVehicle,
  onUpdate,
  onError,
  customer,
  showSalesInfo = true,
  canEdit = true,
  bookingWorkflows = [],
  bookingStatus,
  onAssignWorkflow,
  bookingId
}: BookingServiceListProps) {
  const { selectedBusiness } = useBusiness();
  const { currentResource } = usePermissions();
  const isHomeServices = selectedBusiness?.industry === 'Home Services';
  const [isSelectVehicleModalOpen, setIsSelectVehicleModalOpen] = useState(false);
  const [isSelectServiceModalOpen, setIsSelectServiceModalOpen] = useState(false);
  const [isCreateVehicleModalOpen, setIsCreateVehicleModalOpen] = useState(false);
  const [isBookingPhotoCaptureOpen, setIsBookingPhotoCaptureOpen] = useState(false);
  const [currentVehicleId, setCurrentVehicleId] = useState<string | undefined>(undefined);
  const [currentBookingId, setCurrentBookingId] = useState<string | undefined>(undefined);
  const [currentVehicleTitle, setCurrentVehicleTitle] = useState<string>('');
  const [photoCountByVehicle, setPhotoCountByVehicle] = useState<Record<string, number>>({});
  const [addDropdownOpen, setAddDropdownOpen] = useState<string | null>(null);
  // Service rows don't carry the VIN, so load it per vehicle to know whether
  // the vehicle was decoded (valid VIN saved) or still needs a scan.
  const [vinByVehicle, setVinByVehicle] = useState<Record<string, string | null>>({});
  const [vinScanVehicleId, setVinScanVehicleId] = useState<string | null>(null);

  const resolvedBookingId = bookingId ?? services[0]?.booking_id;

  useEffect(() => {
    loadVehicleVins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services]);

  const loadVehicleVins = async () => {
    if (!supabase) return;
    const ids = [...new Set(services.map(s => s.vehicle_id).filter(id => id && id !== 'null'))];
    if (ids.length === 0) return;
    try {
      const { data } = await supabase
        .from('customer_vehicles')
        .select('id, vin')
        .in('id', ids);
      if (data) {
        setVinByVehicle(Object.fromEntries(data.map((v: { id: string; vin: string | null }) => [v.id, v.vin])));
      }
    } catch (err) {
      console.error('Error loading vehicle VINs:', err);
    }
  };

  /** Scanned VIN → immediately open the vehicle modal, which shows the decode in progress (same flow as scanning from vehicle details). */
  const handleVinScanned = (vin: string) => {
    const vehicleId = vinScanVehicleId;
    setVinScanVehicleId(null);
    if (!vehicleId) return;
    onEditVehicle?.(vehicleId, { scannedVin: vin });
  };

  useEffect(() => {
    if (!addDropdownOpen) return;
    const handleClick = () => setAddDropdownOpen(null);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [addDropdownOpen]);

  // Group services by vehicle
  const servicesByVehicle = services.reduce<Record<string, BookingService[]>>((acc, service) => {
    const vehicleId = service.vehicle_id;
    if (!acc[vehicleId]) {
      acc[vehicleId] = [];
    }
    acc[vehicleId].push(service);
    return acc;
  }, {});

  useEffect(() => {
    loadPhotoCountsForVehicles();
  }, [services]);

  const loadPhotoCountsForVehicles = async () => {
    if (!supabase) return;

    try {
      const counts: Record<string, number> = {};
      
      for (const service of services) {
        if (!service.vehicle_id || service.vehicle_id === 'null') continue;
        const photos = await getBookingPhotos(service.booking_id, service.vehicle_id);
        counts[service.vehicle_id] = photos.length;
      }

      setPhotoCountByVehicle(counts);
    } catch (err) {
      console.error('Error loading photo counts:', err);
    }
  };

  const handleVehicleSelected = (vehicle: CustomerVehicle) => {
    setIsSelectVehicleModalOpen(false);
    setCurrentVehicleId(undefined);
    setCurrentBookingId(undefined);
    if (onAddService) {
      onAddService(vehicle.id);
    }
  };

  const handleVehicleCreated = async (vehicle: any) => {
    // Update customer data to get the new vehicle
    const updatedCustomer = await getCustomer(customer?.id || '');
    
    // Set the new vehicle as current and open service selection
    setCurrentVehicleId(vehicle.id);
    setIsCreateVehicleModalOpen(false);
    setIsSelectServiceModalOpen(true);
  };

  // Update the photo count after modal closes
  const handleBookingPhotoCaptureClose = () => {
    setIsBookingPhotoCaptureOpen(false);
    setCurrentVehicleId(undefined);
    setCurrentBookingId(undefined);
    setCurrentVehicleTitle('');
    loadPhotoCountsForVehicles();
  };

  return (
    <div className="space-y-2">
      {/* For home services with no services yet, show an Add button */}
      {isHomeServices && Object.keys(servicesByVehicle).length === 0 && canEdit && (
        <div className="p-2 relative">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setAddDropdownOpen(addDropdownOpen === 'home' ? null : 'home'); }}
            className="w-full flex items-center justify-center px-3 py-2.5 bg-gray-50 hover:bg-gray-100 active:bg-gray-200 rounded-[10px] text-[13px] font-medium text-gray-700 transition-all duration-150 active:scale-[0.98] touch-manipulation"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Add
          </button>
          {addDropdownOpen === 'home' && (
            <div
              className="absolute left-2 right-2 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-[200] overflow-hidden"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => {
                  setAddDropdownOpen(null);
                  setCurrentVehicleId(undefined);
                  setIsSelectServiceModalOpen(true);
                }}
                className="w-full flex items-center px-4 py-2.5 text-[13px] font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors"
              >
                <Plus className="h-3.5 w-3.5 mr-2 text-gray-500" />
                Add Service
              </button>
              {onAddProposal && (
                <button
                  type="button"
                  onClick={() => {
                    setAddDropdownOpen(null);
                    onAddProposal();
                  }}
                  className="w-full flex items-center px-4 py-2.5 text-[13px] font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors border-t border-gray-100"
                >
                  <Plus className="h-3.5 w-3.5 mr-2 text-gray-500" />
                  Add Proposal
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {Object.entries(servicesByVehicle).map(([vehicleId, vehicleServices]) => {
        const vehicle = vehicleServices[0];
        const photoCount = photoCountByVehicle[vehicleId] || 0;
        const vinDecoded = isPlausibleVin(vinByVehicle[vehicleId] || '');
        
        return (
          <div key={vehicleId} className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-visible">
            {/* Vehicle - its own boxed card so it reads as a distinct object */}
            <div className="p-2 border-b border-gray-200/60">
              <div
                className={`bg-gray-50 border border-gray-200 rounded-[10px] px-3 py-2.5 relative ${
                  canEdit && onEditVehicle ? 'cursor-pointer active:bg-gray-100 touch-manipulation' : ''
                } transition-all duration-150`}
                onClick={(e) => {
                  // Prevent triggering when clicking on the photo button
                  if ((e.target as HTMLElement).closest('button')) return;
                  if (canEdit && onEditVehicle) {
                    onEditVehicle(vehicleId);
                  }
                }}
                style={{
                  WebkitTapHighlightColor: 'transparent',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif'
                }}
              >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <div className="text-[13px] font-semibold text-gray-900 truncate">
                      {vehicle.vehicle_year} {vehicle.vehicle_make} {vehicle.vehicle_model}
                    </div>
                    {vinDecoded && (
                      <span
                        className="flex-shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-600"
                        title="Decoded from VIN"
                      >
                        <ScanLine className="h-3 w-3" />
                        <Check className="h-3 w-3" strokeWidth={3} />
                      </span>
                    )}
                  </div>
                  {vehicle.vehicle_color && (
                    <div className="text-[11px] text-gray-600 mt-0.5">{vehicle.vehicle_color}</div>
                  )}
                </div>
                {canEdit && !vinDecoded && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setVinScanVehicleId(vehicleId);
                    }}
                    className="h-8 w-8 flex items-center justify-center bg-blue-50 text-blue-600 rounded-full hover:bg-blue-100 active:bg-blue-200 active:scale-95 transition-all duration-150 touch-manipulation flex-shrink-0 ml-2 shadow-sm"
                    title="Scan VIN"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <ScanLine className="h-4 w-4" />
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentVehicleId(vehicleId);
                    setCurrentBookingId(vehicle.booking_id);
                    setCurrentVehicleTitle(`${vehicle.vehicle_year || ''} ${vehicle.vehicle_make || ''} ${vehicle.vehicle_model || ''}`.trim() || 'Vehicle');
                    setIsBookingPhotoCaptureOpen(true);
                  }}
                  className="relative h-8 w-8 flex items-center justify-center bg-blue-50 text-blue-600 rounded-full hover:bg-blue-100 active:bg-blue-200 active:scale-95 transition-all duration-150 touch-manipulation flex-shrink-0 ml-2 shadow-sm"
                  title={photoCount > 0 ? `${photoCount} photos` : 'Add photos'}
                  style={{ 
                    WebkitTapHighlightColor: 'transparent',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif'
                  }}
                >
                  <Camera className="h-4 w-4" />
                  {photoCount > 0 && (
                    <span className="absolute -top-1 -right-1 px-1.5 py-0.5 bg-blue-600 text-white rounded-full text-[10px] font-bold min-w-[16px] text-center leading-tight shadow-sm">
                      {photoCount}
                    </span>
                  )}
                </button>
              </div>
              </div>
            </div>

            {/* Services List */}
            <div className="divide-y divide-gray-200/60">
              {vehicleServices.map((service) => {
                return (
                  <ServiceWithWorkflowToggle
                    key={service.id}
                    service={service}
                    bookingStatus={bookingStatus || 'pending'}
                    onUpdate={onUpdate}
                    onError={onError}
                    showSalesInfo={showSalesInfo}
                    canEdit={canEdit}
                    bookingId={bookingId}
                  />
                );
              })}
            </div>
            
            {/* Add Button with Dropdown */}
            {canEdit && (
              <div className="px-3 py-2 border-t border-gray-200/60 relative">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setAddDropdownOpen(addDropdownOpen === vehicleId ? null : vehicleId); }}
                  className="w-full flex items-center justify-center px-3 py-2 bg-gray-50 hover:bg-gray-100 active:bg-gray-200 rounded-[10px] text-[13px] font-medium text-gray-700 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <Plus className="h-4 w-4 mr-1.5" />
                  Add
                </button>
                {addDropdownOpen === vehicleId && (
                  <div
                    className="absolute left-3 right-3 bottom-full mb-1 bg-white border border-gray-200 rounded-xl shadow-lg z-[200] overflow-hidden"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setAddDropdownOpen(null);
                        setCurrentVehicleId(vehicleId);
                        setIsSelectServiceModalOpen(true);
                      }}
                      className="w-full flex items-center px-4 py-2.5 text-[13px] font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors"
                    >
                      <Plus className="h-3.5 w-3.5 mr-2 text-gray-500" />
                      Add Service
                    </button>
                    {onAddProposal && (
                      <button
                        type="button"
                        onClick={() => {
                          setAddDropdownOpen(null);
                          onAddProposal();
                        }}
                        className="w-full flex items-center px-4 py-2.5 text-[13px] font-medium text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors border-t border-gray-100"
                      >
                        <Plus className="h-3.5 w-3.5 mr-2 text-gray-500" />
                        Add Proposal
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Add Vehicle Button - iOS Style */}
      {canEdit && !isHomeServices && (
        <div className="p-2">
          <button
            type="button"
            onClick={() => setIsSelectVehicleModalOpen(true)}
            className="w-full flex items-center justify-center px-3 py-2.5 bg-gray-50 hover:bg-gray-100 active:bg-gray-200 rounded-[10px] text-[13px] font-medium text-gray-700 transition-all duration-150 active:scale-[0.98] touch-manipulation"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Add Vehicle
          </button>
        </div>
      )}

      <SelectVehicleModal
        isOpen={isSelectVehicleModalOpen}
        onClose={() => setIsSelectVehicleModalOpen(false)}
        onSelect={handleVehicleSelected}
        vehicles={customer?.vehicles || []}
        selectedVehicles={services.map(s => ({
          id: s.vehicle_id,
          customer_id: customer?.id || '',
          make: s.vehicle_make || '',
          model: s.vehicle_model || '',
          year: s.vehicle_year || new Date().getFullYear(),
          vin: '',
          color: '',
          license_plate: '',
          mileage: 0,
          notes: '',
          is_primary: false,
          last_service: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          deleted_at: null
        }))}
        onAddVehicle={() => {
          setIsSelectVehicleModalOpen(false);
          setCurrentVehicleId(undefined);
          setIsCreateVehicleModalOpen(true);
        }}
      />

      <CreateVehicleModal
        isOpen={isCreateVehicleModalOpen}
        onClose={() => setIsCreateVehicleModalOpen(false)}
        onSuccess={handleVehicleCreated}
        customerId={customer?.id || ''}
      />

      <SelectServiceModal
        isOpen={isSelectServiceModalOpen}
        onClose={() => setIsSelectServiceModalOpen(false)}
        onAdd={async (service, priceOption) => {
          try {
            if (!resolvedBookingId) {
              throw new Error('Missing booking id — cannot add service');
            }
            // For home services, vehicle_id can be null
            if (!isHomeServices && !currentVehicleId) {
              throw new Error('No vehicle selected');
            }

            const pricingType = priceOption.pricing_type || 'flat';
            const isPerUnit = pricingType === 'per_unit' || pricingType === 'per_sqft';

            // Calculate tax amounts with proper currency rounding
            const taxRate = service.tax_exempt ? 0 : 5; // Default tax rate
            const taxAmount = service.tax_exempt ? 0 : Math.round((priceOption.price * taxRate / 100) * 100) / 100;
            const totalAmount = Math.round((priceOption.price + taxAmount) * 100) / 100;

            // Insert new service
            const { error: serviceError } = await supabase!
              .from('booking_services')
              .insert({
                business_id: selectedBusiness?.id,
                booking_id: resolvedBookingId,
                service_id: service.id,
                vehicle_id: isHomeServices ? null : currentVehicleId,
                name: service.name,
                description: service.description || '',
                price: isPerUnit ? 0 : priceOption.price,
                tax_exempt: service.tax_exempt || false,
                tax_rate: taxRate,
                tax_amount: isPerUnit ? 0 : taxAmount,
                total_amount: isPerUnit ? 0 : totalAmount,
                duration_minutes: isPerUnit ? 0 : priceOption.duration_minutes,
                status: 'pending',
                pricing_type: pricingType,
                unit_price: isPerUnit ? (priceOption.unit_price || 0) : null,
                duration_per_unit: isPerUnit ? (priceOption.duration_per_unit || 0) : null,
                quantity: isPerUnit ? 0 : 1,
                compare_at_price: (priceOption as any).compare_at_price ?? null,
                ...upsellLineStamp(staffResourceId(currentResource)),
              });

            if (serviceError) throw serviceError;

            // Note: Removed PDF generation code - no longer using the old invoicing system

            // Refresh the booking data
            await onUpdate();

            // Close modal
            setIsSelectServiceModalOpen(false);
            setCurrentVehicleId(undefined);
          } catch (err) {
            console.error('Error adding service:', err);
            onError(err instanceof Error ? err.message : 'Failed to add service');
            return;
          }
        }}
        bookingId={resolvedBookingId}
        vehicleId={currentVehicleId}
      />

      <BookingPhotoCapture
        isOpen={isBookingPhotoCaptureOpen}
        onClose={handleBookingPhotoCaptureClose}
        vehicleTitle={currentVehicleTitle}
        vehicleId={currentVehicleId || ''}
        bookingId={currentBookingId || ''}
        onPhotosUpdated={loadPhotoCountsForVehicles}
      />

      <VinScanModal
        isOpen={!!vinScanVehicleId}
        businessId={selectedBusiness?.id || ''}
        onClose={() => setVinScanVehicleId(null)}
        onScanned={(vin) => void handleVinScanned(vin)}
      />
    </div>
  );
}